import { useAuth } from '@/app/context';
import type { BusinessRisk } from '@/services/quotes/types';
import { getInsuranceTypeLabel } from '@/shared/enums/insurance-type.enum';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { useNavigation, useRequirePermissions } from '@/shared/hooks';
import { toaster } from '@/shared/ui/toaster.component';
import { downloadBlob } from '@/shared/utils';
import {
  formatRegisterNumber,
  isRegisterNumberCnpjMask,
  resolveRegisterNumber,
} from '@/shared/utils/format-register-number';
import { getQuoteStatus } from '@/shared/utils/get-quote-status..util';
import {
  Button,
  Spinner,
  Text,
  Tooltip,
} from '@noorden-insurtech/alba-design-system';
import { formOptions } from '@tanstack/react-form';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  FileDown,
  Mail,
  SquarePen,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelectedBrokerWriteGuard } from '../../../brokers/hooks';
import { PremiumCard } from '../../../proposals/components/proposal-steps/premium-card.component';
import { useQuoteParams } from '../../context/quote-params.context';
import { useQuoteSummary } from '../../context/quote-summary.context';
import { useUnderwritingMessages } from '../../hooks/business/use-underwriting-messages.hook';
import { useInitiateProposal } from '../../hooks/common/use-initiate-proposal.hook';
import { withForm } from '../../hooks/common/use-quote-form.hook';
import { useSendProposalEmail } from '../../hooks/common/use-send-proposal-email.hook';
import { useCoverages } from '../../hooks/homeowner/use-coverages.hook';
import { useGenerateProposalPdf } from '../../hooks/homeowner/use-generate-proposal-pdf.hook';
import { usePlanResourcesByCode } from '../../hooks/homeowner/use-plan-resources-by-code.hook';
import { useProposalDetails } from '../../hooks/homeowner/use-proposal-details.hook';
import { useValidateInspectionJourney } from '../../hooks/homeowner/use-validate-inspection-journey.hook';
import {
  EditableCoverageCard,
  type EditableCoverageItem,
} from '../editable-coverage-card.component';
import { getInsuranceType } from '../../utils/getInsuranceType';
import { useBusinessActivities } from '../../hooks/business/use-business-activities.hook';
import {
  formatUnderwritingMessages,
  resolveBusinessActivityNames,
} from '../../utils/format-underwriting-messages.util';
import { mapBusinessRisksToLocations } from '../../utils/map-business-risks-to-locations.util';
import { type BusinessLocation } from './business-location';
import { BusinessLocationCard } from './business-location-card.component';
import { GeneratePdfDialog } from './generate-pdf.dialog';
import { InspectionJourneyModal } from './inspection-journey-modal.component';
import { SendEmailDialog } from './send-email.dialog';
import { UnderAnalysisInfoModal } from './under-analysis-info-modal.component';

const formOpts = formOptions({
  defaultValues: {},
});

type EditableCoverageSection = {
  title: string;
  items: EditableCoverageItem[];
};

type CommercializationFormValues = {
  brokerName?: string;
  commissionPercent?: string;
  participationPercent?: string;
  coBrokers?: Array<{
    coBrokerId?: string;
    coBrokerName?: string;
    participationPercent?: string;
  }>;
};

function requiresInspection(
  value?: boolean | 'S' | 'N' | string | null
): boolean {
  return value === true || value === 'S';
}

function resolveDisplayedCommissionRate(proposalData?: {
  commissionRate?: number;
  CommissionRate?: number;
  agents?: Array<{ commissionRate?: number }>;
}) {
  const agentCommissionRate = proposalData?.agents?.[0]?.commissionRate;
  if (
    typeof agentCommissionRate === 'number' &&
    !Number.isNaN(agentCommissionRate)
  ) {
    return agentCommissionRate;
  }

  const policyCommissionRate =
    proposalData?.commissionRate ?? proposalData?.CommissionRate;

  if (
    typeof policyCommissionRate === 'number' &&
    !Number.isNaN(policyCommissionRate)
  ) {
    return policyCommissionRate * 100;
  }

  return undefined;
}

function formatCurrency(value?: number) {
  if (value === undefined || value === null) return '';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return dateStr;
}

function resolvePurePremium(proposalData?: {
  lobs?: Array<{
    risks?: Array<{
      coverages?: Array<{
        productElementCode?: string;
        purePremium?: number;
      }>;
    }>;
  }>;
}) {
  const coverages = proposalData?.lobs?.[0]?.risks?.[0]?.coverages ?? [];

  const assistanceCoverage = coverages.find(
    coverage => coverage.productElementCode === 'ASSIST'
  );

  return (
    assistanceCoverage?.purePremium ??
    coverages.find(coverage => coverage.purePremium !== undefined)?.purePremium
  );
}

export const StepCalculation = withForm({
  ...formOpts,
  props: {
    onBack: () => {},
    isViewMode: false,
    onResumeEdit: undefined as (() => void) | undefined,
  },
  render: ({ form, onBack, isViewMode, onResumeEdit }) => {
    const { attributes } = useAuth();
    const [showAssistanceDetails, setShowAssistanceDetails] = useState(false);
    const [isGeneratePdfOpen, setIsGeneratePdfOpen] = useState(false);
    const [isSendEmailOpen, setIsSendEmailOpen] = useState(false);
    const [isSendingEmails, setIsSendingEmails] = useState(false);
    const [isInspectionJourneyModalOpen, setIsInspectionJourneyModalOpen] =
      useState(false);
    const [isUnderAnalysisModalOpen, setIsUnderAnalysisModalOpen] =
      useState(false);
    const hasShownUnderAnalysisModalRef = useRef(false);
    const [expandedLocationIds, setExpandedLocationIds] = useState<Set<string>>(
      new Set()
    );
    const [isInstallmentExpanded, setIsInstallmentExpanded] = useState(true);
    const { policyId, category, productId } = useQuoteParams();
    const isBusinessQuote = category === 'BUSINESS_INSURANCE';
    const { navigate, replace } = useNavigation();
    const { hasPermission: canWriteQuote } = useRequirePermissions({
      permissions: ['policies.quotes.write'],
    });
    const quoteWriteGuard = useSelectedBrokerWriteGuard('quote');
    const proposalWriteGuard = useSelectedBrokerWriteGuard('proposal');
    const quoteActionGuard = useSelectedBrokerWriteGuard('quote', 'other');
    const versionDate = '2025-11-03';
    // Catálogo por produto usado apenas no fluxo residencial; o empresarial
    // lista as coberturas por atividade (useBusinessCoverages) nos cards de local.
    const { data: coveragesCatalog } = useCoverages(versionDate, {
      enabled: !isBusinessQuote,
    });
    const generatePdfMutation = useGenerateProposalPdf();
    const initiateProposalMutation = useInitiateProposal();
    const sendProposalEmailMutation = useSendProposalEmail();
    const validateInspectionJourneyMutation = useValidateInspectionJourney();
    const { updatePremiumInfo, updateInsuranceData, summary } =
      useQuoteSummary();

    const {
      data: proposalData,
      isLoading: isLoadingProposal,
      refetch: refetchProposal,
    } = useProposalDetails(policyId);

    const isUnderAnalysis =
      Number(proposalData?.statusPortal) === PortalStatus.QuoteUnderAnalysis;

    const proponentRegisterNumber = resolveRegisterNumber(
      proposalData?.customer?.cpf,
      proposalData?.customer?.idNo
    );
    const isProponentCnpj = isRegisterNumberCnpjMask(proponentRegisterNumber);
    const proponentRegisterNumberLabel = isProponentCnpj ? 'CNPJ' : 'CPF';

    const { data: underwritingData } = useUnderwritingMessages(
      policyId,
      isBusinessQuote && isUnderAnalysis && Boolean(policyId)
    );

    const { data: businessActivitiesCatalog } = useBusinessActivities({
      enabled: isBusinessQuote && isUnderAnalysis,
    });

    const underAnalysisMessages = useMemo(() => {
      if (!isBusinessQuote || !isUnderAnalysis) return undefined;
      const risks = (proposalData?.lobs?.[0]?.risks ?? []) as BusinessRisk[];
      const activityCodes = risks
        .map(
          risk =>
            risk.activityTypePerformed || risk.tipoDeAtividadeRealizada || ''
        )
        .filter(Boolean);
      return formatUnderwritingMessages(underwritingData?.messages ?? [], {
        activities: resolveBusinessActivityNames(
          activityCodes,
          businessActivitiesCatalog
        ),
        hasUnderwritingDiscountRequest: risks.some(
          risk => risk.underwritingDiscountRequestInd === 'Y'
        ),
      });
    }, [
      isBusinessQuote,
      isUnderAnalysis,
      proposalData?.lobs,
      underwritingData?.messages,
      businessActivitiesCatalog,
    ]);

    useEffect(() => {
      if (
        !hasShownUnderAnalysisModalRef.current &&
        isBusinessQuote &&
        isUnderAnalysis
      ) {
        hasShownUnderAnalysisModalRef.current = true;
        setIsUnderAnalysisModalOpen(true);
      }
    }, [isBusinessQuote, isUnderAnalysis]);

    useEffect(() => {
      if (proposalData) {
        const resolvedNetPremium = proposalData.rawPremium;

        const resolvedIof = proposalData.iof ?? proposalData.IOF;

        const resolvedInterest = proposalData.pisCofins;

        const resolvedTotalPremium =
          proposalData.grossPremium ??
          proposalData.GrossPremium ??
          proposalData.DuePremium;

        updatePremiumInfo({
          quoteNumber:
            proposalData.quotationNo ||
            proposalData.proposalNo ||
            proposalData.policyId,
          purePremium: formatCurrency(resolvePurePremium(proposalData)),
          premium: formatCurrency(resolvedNetPremium),
          interest: formatCurrency(resolvedInterest),
          iof: formatCurrency(resolvedIof),
          totalPremium: formatCurrency(resolvedTotalPremium),
          product: getInsuranceType(category),
          status: proposalData.statusPortal || proposalData.proposalStatus,
        });

        // E-mail (insertByUserName) só para o resumo; o front não persiste.
        const inclusionUserFromPolicy =
          proposalData.insertByUserName?.trim() || '';
        if (inclusionUserFromPolicy) {
          updateInsuranceData({ inclusionUser: inclusionUserFromPolicy });
        }
      }
    }, [proposalData, category, updatePremiumInfo, updateInsuranceData]);

    const totalPremiumValue = useMemo(() => {
      if (!proposalData) return 'R$ 0,00';
      const value =
        proposalData.grossPremium ??
        proposalData.GrossPremium ??
        proposalData.DuePremium;
      if (value == null) return '-';
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value);
    }, [proposalData]);

    const liquidValue = useMemo(() => {
      const raw = proposalData?.rawPremium;
      return raw != null ? formatCurrency(raw) : '-';
    }, [proposalData?.rawPremium]);

    const iofFormatted = useMemo(() => {
      const iof = proposalData?.iof ?? proposalData?.IOF ?? 0;
      return formatCurrency(iof);
    }, [proposalData]);

    const interestValue = useMemo(() => {
      return formatCurrency(proposalData?.pisCofins ?? 0);
    }, [proposalData?.pisCofins]);

    const proposalNumber =
      proposalData?.quotationNo || proposalData?.proposalNo || '';
    const resolvedQuoteStatus =
      Number(proposalData?.statusPortal) ||
      Number(proposalData?.policyStatus) ||
      1;

    const coveragesEditable: EditableCoverageSection[] = useMemo(() => {
      if (!proposalData) {
        return [];
      }

      const coveragesFromApi =
        proposalData.lobs?.[0]?.risks?.[0]?.coverages || [];

      const resolveCoverageLabel = (code?: string) => {
        const resolvedCode = (code || '').trim();
        if (!resolvedCode) return 'Cobertura';
        const match = coveragesCatalog?.find(cov => cov.code === resolvedCode);
        return match?.name?.trim() || resolvedCode;
      };

      const resolveDeductibleLabel = (
        productElementCode?: string,
        codeDeductible?: number
      ): string => {
        if (!codeDeductible || !productElementCode) return '';
        const catalogCoverage = coveragesCatalog?.find(
          cov => cov.code === productElementCode
        );
        if (!catalogCoverage) return String(codeDeductible);
        const deductible = catalogCoverage.deductibles?.find(
          d => String(d.code) === String(codeDeductible)
        );
        return deductible?.name || String(codeDeductible);
      };

      const isExcludedCoverage = (code?: string) => {
        const upper = (code || '').trim().toUpperCase();
        return (
          upper.endsWith('ASS') ||
          upper.endsWith('CAP') ||
          upper === 'ASSIST' ||
          upper === 'CAPITA'
        );
      };

      const items: EditableCoverageItem[] = coveragesFromApi
        .filter(coverage => !isExcludedCoverage(coverage.productElementCode))
        .map(coverage => ({
          description: resolveCoverageLabel(coverage.productElementCode),
          limiteLabel: 'Limite Máximo de Indenização*',
          limitePlaceholder: 'R$ 000,00',
          limiteValue: coverage.sumInsured
            ? new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(coverage.sumInsured)
            : '',
          premioLabel: 'Franquia*',
          premioPlaceholder:
            'Franquia de 1%  Com limite entre R$ 0,01 e R$ 1.000',
          premioValue: resolveDeductibleLabel(
            coverage.productElementCode,
            coverage.codeDeductible
          ),
        }));

      return items.length > 0 ? [{ title: 'Coberturas', items }] : [];
    }, [proposalData, coveragesCatalog]);

    const resolvedPlanCode = useMemo(() => {
      const coverages = proposalData?.lobs?.[0]?.risks?.[0]?.coverages || [];
      const assistCov = coverages.find(
        cov => cov.productElementCode === 'ASSIST'
      );
      if (assistCov?.planCode) return assistCov.planCode;
      const capitaCov = coverages.find(
        cov => cov.productElementCode === 'CAPITA'
      );
      return capitaCov?.planCode || '';
    }, [proposalData]);

    const { data: planResources } = usePlanResourcesByCode(
      resolvedPlanCode || undefined
    );

    const installmentRows = useMemo(() => {
      const payments = proposalData?.paymentInfo;
      if (!payments?.length) return [];

      const sorted = [...payments].sort(
        (a, b) => a.installmentPeriodCount - b.installmentPeriodCount
      );

      return sorted.map(pi => {
        const count = pi.installmentPeriodCount;
        const firstInstallment = pi.installmentList?.[0];
        const totalPremium =
          pi.installmentList?.reduce(
            (sum, inst) => sum + (inst.installmentAmount ?? 0),
            0
          ) ?? 0;
        const firstAmount = firstInstallment?.installmentAmount ?? 0;
        // Da segunda parcela em diante o valor é o mesmo; ela representa o
        // "valor das demais parcelas" exibido no tooltip.
        const secondAmount = pi.installmentList?.[1]?.installmentAmount ?? 0;

        const hasInterest =
          pi.installmentType !== '-1' && pi.installmentType !== '0';

        return {
          label: count === 1 ? 'À vista' : `1+${count - 1}`,
          interestLabel: hasInterest ? `${pi.installmentType}%` : 'Sem Juros',
          installmentValue: formatCurrency(firstAmount),
          remainingInstallmentsValue:
            count > 1 && secondAmount > 0 ? formatCurrency(secondAmount) : null,
          totalValue: formatCurrency(totalPremium),
          rawInstallmentValue: firstAmount,
          rawTotalValue: totalPremium,
        };
      });
    }, [proposalData?.paymentInfo]);

    const hasNonZeroInstallments = useMemo(
      () =>
        installmentRows.some(
          row => row.rawInstallmentValue > 0 || row.rawTotalValue > 0
        ),
      [installmentRows]
    );

    const assistanceItems = useMemo(() => {
      if (!Array.isArray(planResources) || planResources.length === 0) {
        return summary?.assistance?.assistances?.length
          ? summary.assistance.assistances
          : [];
      }

      return planResources
        .filter(resource => {
          const code = String(resource.code || '')
            .trim()
            .toUpperCase();
          if (code === 'CAPITA') return false;
          const name = resource.name?.trim();
          if (!name) return false;
          const normalized = name.toLowerCase();
          if (
            normalized.includes('sorteio') ||
            normalized.includes('capitaliz')
          )
            return false;
          return true;
        })
        .map(resource => resource.name?.trim())
        .filter((name): name is string => Boolean(name));
    }, [planResources, summary?.assistance?.assistances]);

    const capitaItems = useMemo(() => {
      if (!Array.isArray(planResources) || planResources.length === 0) {
        return summary?.assistance?.raffles?.length
          ? summary.assistance.raffles
          : [];
      }

      const capitaIndex = planResources.findIndex(
        r =>
          String(r.code || '')
            .trim()
            .toUpperCase() === 'CAPITA'
      );

      if (capitaIndex < 0) return [];

      return planResources
        .slice(capitaIndex + 1)
        .map(r => r.name?.trim())
        .filter((name): name is string => Boolean(name));
    }, [planResources, summary?.assistance?.raffles]);

    const businessLocationCards = useMemo<BusinessLocation[]>(() => {
      if (!isBusinessQuote || !proposalData?.lobs?.[0]?.risks?.length) {
        return [];
      }
      return mapBusinessRisksToLocations(
        proposalData.lobs[0].risks as BusinessRisk[],
        { stableIds: true }
      );
    }, [isBusinessQuote, proposalData]);

    const allLocationsExpanded =
      businessLocationCards.length > 0 &&
      businessLocationCards.every(loc => expandedLocationIds.has(loc.id));

    const toggleLocation = (key: string) => {
      setExpandedLocationIds(prev => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    };

    const toggleAllLocations = () => {
      if (allLocationsExpanded) {
        setExpandedLocationIds(new Set());
      } else {
        setExpandedLocationIds(
          new Set(businessLocationCards.map(loc => loc.id))
        );
      }
    };

    const commercializationData = useMemo(() => {
      const commercializationFormValues = form.state
        .values as CommercializationFormValues;
      const localCoBrokers = Array.isArray(
        commercializationFormValues.coBrokers
      )
        ? commercializationFormValues.coBrokers
        : [];
      const hasLocalCommercialization =
        !isViewMode &&
        (!!commercializationFormValues.brokerName ||
          !!commercializationFormValues.commissionPercent ||
          !!commercializationFormValues.participationPercent ||
          localCoBrokers.length > 0);
      const agents = hasLocalCommercialization
        ? [
            {
              name: commercializationFormValues.brokerName,
              shareRate:
                Number(
                  commercializationFormValues.participationPercent?.replace(
                    ',',
                    '.'
                  )
                ) / 100,
            },
            ...localCoBrokers
              .filter(
                coBroker =>
                  coBroker.coBrokerName?.trim() &&
                  coBroker.participationPercent?.trim() &&
                  Number(
                    (coBroker.participationPercent || '').replace(',', '.')
                  ) > 0
              )
              .filter((coBroker, index, array) => {
                const key = (coBroker.coBrokerId || coBroker.coBrokerName || '')
                  .trim()
                  .toLowerCase();
                return (
                  array.findIndex(item => {
                    const itemKey = (item.coBrokerId || item.coBrokerName || '')
                      .trim()
                      .toLowerCase();
                    return itemKey === key;
                  }) === index
                );
              })
              .map(coBroker => ({
                name: coBroker.coBrokerName,
                shareRate:
                  Number(
                    (coBroker.participationPercent || '').replace(',', '.')
                  ) / 100,
              })),
          ]
        : (proposalData?.agents || []).filter(
            (agent, index) => index === 0 || (agent?.shareRate || 0) > 0
          );
      const commissionRate = hasLocalCommercialization
        ? Number(
            commercializationFormValues.commissionPercent?.replace(',', '.')
          ) || 0
        : resolveDisplayedCommissionRate(proposalData);

      const formatPercent = (value?: number) =>
        typeof value === 'number' && !Number.isNaN(value)
          ? `${(value * 100).toFixed(2)}%`
          : '—';

      return {
        commissionRate:
          commissionRate !== undefined ? `${commissionRate.toFixed(2)}%` : '—',
        agents: agents.map((agent, index) => ({
          label: index === 0 ? 'Corretor' : `Co-corretor ${index}`,
          name: agent?.name || '—',
          participation: formatPercent(agent?.shareRate),
        })),
      };
    }, [form.state.values, isViewMode, proposalData]);

    // Para business o motor de cálculo ainda não está configurado; libera a
    // criação de proposta independente de status/data até o cálculo existir.
    const canCreateProposal =
      isBusinessQuote ||
      Boolean(
        proposalData?.quotationDate &&
        new Date(proposalData.quotationDate) >
          new Date(Date.now() - 15 * 24 * 60 * 60 * 1000) &&
        proposalData?.statusPortal === String(PortalStatus.QuoteCalculated)
      );
    const canEditQuote = canWriteQuote && canCreateProposal;

    const handleEditQuote = () => {
      if (onResumeEdit) {
        onResumeEdit();
        return;
      }

      if (!policyId) return;

      navigate({
        to: '/quotes/$policyId/edit',
        params: { policyId },
        search: { mode: 'edit' },
      });
    };

    const handleInitiateProposal = async () => {
      if (!policyId) {
        toaster.create({
          type: 'error',
          title: 'Não foi possível criar a proposta',
          description: 'ID da cotação (PolicyId) não encontrado.',
        });
        return;
      }

      try {
        const data = await initiateProposalMutation.mutateAsync({
          policyId,
        });

        toaster.create({
          type: 'success',
          title: 'Proposta criada com sucesso',
          description: data.proposalNo
            ? `Nº da proposta: ${data.proposalNo}`
            : undefined,
        });
        const proposalPolicyId = data.policyId || policyId;
        if (proposalPolicyId) {
          replace('/proposals/$id/edit', { id: proposalPolicyId });
        }
      } catch (error) {
        toaster.create({
          type: 'error',
          title: 'Não foi possível criar a proposta',
          description:
            error instanceof Error ? error.message : 'Erro inesperado',
        });
      }
    };

    const handleInspectionJourneyConfirm = async () => {
      setIsInspectionJourneyModalOpen(false);
      await handleInitiateProposal();
    };

    const handleCreateProposal = async () => {
      if (!policyId) {
        toaster.create({
          type: 'error',
          title: 'Não foi possível validar inspeção',
          description: 'ID da cotação (PolicyId) não encontrado.',
        });
        return;
      }

      try {
        await validateInspectionJourneyMutation.mutateAsync(policyId);
        const { data: refreshedProposal } = await refetchProposal();

        if (requiresInspection(refreshedProposal?.requiresInspection)) {
          setIsInspectionJourneyModalOpen(true);
          return;
        }

        await handleInitiateProposal();
      } catch (error) {
        toaster.create({
          type: 'error',
          title: 'Erro ao validar elegibilidade para inspeção',
          description:
            error instanceof Error ? error.message : 'Erro inesperado',
        });
      }
    };

    return (
      <div className='flex w-full max-w-none flex-col items-start gap-6 px-1 sm:gap-8 sm:px-6'>
        {isLoadingProposal ? (
          <div className='flex justify-center items-center p-8 w-full'>
            <Spinner size='lg' colorScheme='blue' />
          </div>
        ) : null}

        <div className='flex flex-col justify-center items-center p-0 gap-2.5 self-stretch w-full'>
          <PremiumCard
            totalValue={totalPremiumValue}
            liquidValue={liquidValue}
            interestPercentage='0,00%'
            interestValue={interestValue}
            iofValue={iofFormatted}
            product={getInsuranceType(category)}
            proposalNumber={proposalNumber}
            proposalLabel='Número da Cotação'
            status={getQuoteStatus(resolvedQuoteStatus).label}
            showPisCofins={false}
            isUnderAnalysis={isUnderAnalysis}
            underAnalysisMessages={underAnalysisMessages}
          />
        </div>

        {isBusinessQuote && (
          <div className='flex flex-col gap-6 items-start w-full lg:flex-row lg:flex-nowrap'>
            <div className='flex flex-col gap-4 flex-1 min-w-0 w-full'>
              <Text className='text-base text-alba-black-500'>
                Dados do Seguro
              </Text>
              <div className='border border-alba-gray-400 rounded-xl p-4 flex flex-col gap-2 w-full overflow-hidden'>
                <div className='flex items-center gap-2 w-full'>
                  <Text className='text-sm font-bold text-alba-black-500 flex-1 min-w-0'>
                    Início de Vigência
                  </Text>
                  <Text className='text-xs text-alba-gray-600 text-right'>
                    {formatDate(proposalData?.effectiveDate)}
                  </Text>
                </div>
                <div className='w-full h-px bg-alba-gray-200' />
                <div className='flex items-center gap-2 w-full'>
                  <Text className='text-sm font-bold text-alba-black-500 flex-1 min-w-0'>
                    Fim de Vigência
                  </Text>
                  <Text className='text-xs text-alba-gray-600 text-right'>
                    {formatDate(proposalData?.expiryDate)}
                  </Text>
                </div>
                <div className='w-full h-px bg-alba-gray-200' />
                <div className='flex items-center gap-2 w-full'>
                  <Text className='text-sm font-bold text-alba-black-500 flex-1 min-w-0'>
                    Tipo de Seguro
                  </Text>
                  <Text className='text-xs text-alba-gray-600 text-right'>
                    {getInsuranceTypeLabel(proposalData?.typeOfInsurance)}
                  </Text>
                </div>
              </div>
            </div>

            <div className='flex flex-col gap-4 flex-1 min-w-0 w-full'>
              <Text className='text-base text-alba-black-500'>
                Dados do Proponente
              </Text>
              <div className='border border-alba-gray-400 rounded-xl p-4 flex flex-col gap-2 w-full overflow-hidden'>
                <div className='flex items-center gap-2 w-full'>
                  <Text className='text-sm font-bold text-alba-black-500 flex-1 min-w-0'>
                    {proponentRegisterNumberLabel}
                  </Text>
                  <Text className='text-sm text-alba-gray-600 text-right'>
                    {proponentRegisterNumber
                      ? formatRegisterNumber(proponentRegisterNumber)
                      : '—'}
                  </Text>
                </div>
                <div className='w-full h-px bg-alba-gray-200' />
                <div className='flex items-center gap-2 w-full'>
                  <Text className='text-sm font-bold text-alba-black-500 flex-1 min-w-0'>
                    {isProponentCnpj ? 'Razão Social' : 'Nome'}
                  </Text>
                  <Text className='text-xs text-alba-gray-600 text-right'>
                    {proposalData?.customer?.socialName ||
                      proposalData?.customer?.name ||
                      '—'}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        )}

        {isBusinessQuote && (
          <div className='flex flex-col gap-4 w-full'>
            <Text className='text-base text-alba-black-500'>
              Condições comerciais
            </Text>

            {hasNonZeroInstallments && (
              <div className='border border-alba-gray-400 rounded-xl p-4 flex flex-col gap-4 w-full'>
                <div className='flex items-center justify-between w-full'>
                  <Text className='text-sm font-bold text-alba-black-500'>
                    Opções de Parcelamento
                  </Text>
                  <button
                    type='button'
                    onClick={() => setIsInstallmentExpanded(prev => !prev)}
                    disabled={isUnderAnalysis}
                    className='p-2 rounded-lg hover:bg-alba-gray-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent'
                    aria-label={
                      isInstallmentExpanded && !isUnderAnalysis
                        ? 'Recolher parcelamento'
                        : 'Expandir parcelamento'
                    }
                  >
                    {isInstallmentExpanded && !isUnderAnalysis ? (
                      <ChevronUp
                        className='h-4 w-4 text-alba-black-500'
                        strokeWidth={1.6}
                      />
                    ) : (
                      <ChevronDown
                        className='h-4 w-4 text-alba-black-500'
                        strokeWidth={1.6}
                      />
                    )}
                  </button>
                </div>

                {isInstallmentExpanded && !isUnderAnalysis && (
                  <div className='w-full overflow-hidden'>
                    <div className='flex items-center gap-2 rounded-t-xl bg-alba-gray-200 px-3 py-3 sm:rounded-none sm:px-6'>
                      <span className='flex-1 text-[11px] font-bold text-alba-black-500 sm:text-xs'>
                        N de Parcelas
                      </span>
                      <span className='flex-1 text-center text-[11px] font-bold text-alba-black-500 sm:text-xs'>
                        Percentual de Juros
                      </span>
                      <span className='flex-1 text-center text-[11px] font-bold text-alba-black-500 sm:text-xs'>
                        Valor da primeira parcela
                      </span>
                      <span className='flex-1 text-center text-[11px] font-bold text-alba-black-500 sm:text-right sm:text-xs'>
                        Total
                      </span>
                    </div>

                    {installmentRows.map((row, index) => (
                      <div key={row.label}>
                        {index > 0 && (
                          <div className='w-full h-px bg-alba-gray-200' />
                        )}
                        <div className='flex items-center gap-2 px-3 py-4 sm:px-6'>
                          <span className='flex-1 text-xs text-alba-black-500 sm:text-sm'>
                            {row.label}
                          </span>
                          <span className='flex-1 text-center text-xs text-alba-gray-600 sm:text-sm'>
                            {row.interestLabel}
                          </span>
                          {row.remainingInstallmentsValue ? (
                            <Tooltip.Root
                              openDelay={100}
                              closeDelay={0}
                              positioning={{ placement: 'top' }}
                            >
                              <Tooltip.Trigger asChild>
                                <span className='flex-1 cursor-default text-center text-xs text-alba-black-500 sm:text-sm'>
                                  {row.installmentValue}
                                </span>
                              </Tooltip.Trigger>
                              <Tooltip.Positioner>
                                <Tooltip.Content>
                                  <Tooltip.Arrow />
                                  Valor das demais parcelas:{' '}
                                  {row.remainingInstallmentsValue}
                                </Tooltip.Content>
                              </Tooltip.Positioner>
                            </Tooltip.Root>
                          ) : (
                            <span className='flex-1 text-center text-xs text-alba-black-500 sm:text-sm'>
                              {row.installmentValue}
                            </span>
                          )}
                          <span className='flex-1 text-center text-xs text-alba-gray-600 sm:text-right sm:text-sm'>
                            {row.totalValue}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {isBusinessQuote && businessLocationCards.length > 0 && (
          <div className='flex flex-col items-start p-0 gap-4 self-stretch'>
            <div className='flex items-center justify-between w-full'>
              <Text className='text-base text-alba-black-500'>
                Lista de Locais
              </Text>
              <button
                onClick={toggleAllLocations}
                className='flex items-center gap-2 px-4 py-2 rounded-lg text-alba-blue-500 hover:bg-alba-gray-100 transition-colors'
              >
                <span className='text-xs font-medium'>
                  {allLocationsExpanded
                    ? 'Fechar Todos'
                    : `Abrir Todos (${businessLocationCards.length})`}
                </span>
                {allLocationsExpanded ? (
                  <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
                ) : (
                  <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
                )}
              </button>
            </div>

            <div className='flex flex-col gap-4 w-full'>
              {businessLocationCards.map((location, index) => (
                <BusinessLocationCard
                  key={location.id}
                  location={location}
                  locationNumber={index + 1}
                  isOpen={expandedLocationIds.has(location.id)}
                  canEdit={false}
                  onToggleLocation={() => toggleLocation(location.id)}
                  productId={productId}
                />
              ))}
            </div>
          </div>
        )}

        {!isBusinessQuote && (
          <div className='flex flex-col items-start p-0 gap-6 self-stretch w-full'>
            <Text className='text-lg font-bold text-alba-black-500'>
              Coberturas
            </Text>
            <div className='flex flex-col items-start p-0 gap-4 self-stretch'>
              {coveragesEditable.map((coverage, index) => (
                <EditableCoverageCard
                  key={index}
                  title={coverage.title}
                  items={coverage.items}
                  isExpanded={true}
                  showBorder={true}
                  disabled={isViewMode}
                />
              ))}
            </div>
          </div>
        )}

        {!isBusinessQuote && (
        <div className='flex flex-col items-start p-0 gap-6 self-stretch'>
          <Text className='text-lg font-bold text-alba-black-500'>
            Assistências/Sorteio
          </Text>
          <div
            className={`flex flex-col justify-center items-start gap-2 self-stretch rounded-xl border border-alba-blue-500 p-3 sm:p-4 ${
              showAssistanceDetails ? '' : ''
            }`}
          >
            <div className='flex flex-row justify-between items-start p-0 gap-2 self-stretch'>
              <div className='flex flex-col justify-center items-start p-0 gap-2 w-[144px]'>
                <Text className='flex-1 text-sm font-bold text-alba-black-500'>
                  {(() => {
                    const name =
                      summary?.assistance?.packageName?.trim() || 'especial';
                    return name.toLowerCase().startsWith('pacote ')
                      ? name
                      : `Pacote ${name}`;
                  })()}
                </Text>
              </div>

              <div className='flex flex-col justify-end items-end p-0 gap-2'>
                <button
                  onClick={() =>
                    setShowAssistanceDetails(previous => !previous)
                  }
                  className='flex flex-row items-center p-0 gap-2 cursor-pointer bg-transparent border-none'
                >
                  <Text className='text-xs text-alba-gray-600 text-right'>
                    {showAssistanceDetails ? 'Menos Detalhes' : 'Mais Detalhes'}
                  </Text>
                  {showAssistanceDetails ? (
                    <ChevronUp
                      className='h-4 w-4 text-alba-gray-600 shrink-0'
                      strokeWidth={1.6}
                    />
                  ) : (
                    <ChevronDown
                      className='h-4 w-4 text-alba-gray-600 shrink-0'
                      strokeWidth={1.6}
                    />
                  )}
                </button>
              </div>
            </div>

            {showAssistanceDetails && (
              <>
                <div className='w-full h-0 border-t border-alba-gray-300' />
                {assistanceItems.map((item, index) => (
                  <div
                    key={`assist-${index}`}
                    className='flex flex-col justify-center items-start p-0 gap-2 self-stretch'
                  >
                    <div className='flex flex-row items-center p-0 gap-2'>
                      <Text className='text-xs text-alba-black-500'>
                        {item}
                      </Text>
                    </div>
                  </div>
                ))}

                {capitaItems.length > 0 && (
                  <>
                    <div className='w-full h-0 border-t border-alba-gray-300 mt-1' />
                    <Text className='text-xs font-bold text-alba-black-500 mt-1'>
                      Capitalização/Sorteio
                    </Text>
                    {capitaItems.map((item, index) => (
                      <div
                        key={`capita-${index}`}
                        className='flex flex-col justify-center items-start p-0 gap-2 self-stretch'
                      >
                        <div className='flex flex-row items-center p-0 gap-2'>
                          <Text className='text-xs text-alba-black-500'>
                            {item}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>
        )}

        <div className='flex flex-col items-start gap-6 w-full'>
          <Text className='text-base font-normal text-alba-black-500 font-montserrat'>
            Comercialização
          </Text>

          <div className='flex flex-col gap-4 w-full'>
            <div className='border border-alba-gray-400 rounded-xl p-4 flex flex-col gap-2 w-full overflow-hidden'>
              <Text className='text-sm font-bold text-alba-black-500 font-montserrat'>
                Percentual de Corretagem do seguro
              </Text>
              <div className='w-full h-px bg-alba-gray-200' />
              <div className='flex items-center justify-between w-full'>
                <Text className='text-xs text-alba-gray-600 font-montserrat'>
                  Percentual
                </Text>
                <Text className='text-base text-alba-gray-600 font-montserrat'>
                  {commercializationData.commissionRate}
                </Text>
              </div>
            </div>

            {commercializationData.agents.map((agent, index) => (
              <div
                key={index}
                className='border border-alba-gray-400 rounded-xl p-4 flex flex-col gap-2 w-full overflow-hidden'
              >
                <Text className='text-sm font-bold text-alba-black-500 font-montserrat'>
                  {agent.label}
                </Text>
                <div className='w-full h-px bg-alba-gray-200' />
                <div className='flex items-center justify-between w-full'>
                  <Text className='text-xs text-alba-gray-600 font-montserrat'>
                    {agent.name}
                  </Text>
                  <Text className='text-base text-alba-gray-600 font-montserrat'>
                    {agent.participation}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='flex flex-col gap-3 self-stretch px-1 sm:px-6 md:flex-row md:items-center md:justify-between'>
          <Button
            variant='ghost'
            size='md'
            onClick={onBack}
            className='flex h-12 min-w-0 items-center justify-start gap-2 px-0 sm:h-auto'
          >
            <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
            Voltar
          </Button>

          <div className='grid w-full grid-cols-1 gap-2 sm:grid-cols-2 md:w-auto md:flex md:flex-wrap md:justify-end'>
            {canEditQuote ? (
              <Button
                variant='secondary'
                size='sm'
                className='h-10 w-full group flex items-center justify-center gap-2 whitespace-nowrap bg-alba-green-500 text-alba-white-500 [&>svg]:text-alba-white-500 hover:[&>svg]:text-[#129440] md:w-auto'
                onClick={handleEditQuote}
                disabled={isUnderAnalysis || quoteWriteGuard.blocked}
                title={quoteWriteGuard.reason}
              >
                <SquarePen className='w-5 h-5' strokeWidth={2} />
                Editar
              </Button>
            ) : null}

            <Button
              variant='secondary'
              size='sm'
              className='h-10 w-full group flex items-center justify-center gap-2 whitespace-nowrap bg-alba-green-500 text-alba-white-500 [&>svg]:text-alba-white-500 hover:[&>svg]:text-[#129440] md:w-auto'
              onClick={() => {
                setIsGeneratePdfOpen(true);
              }}
              disabled={isUnderAnalysis || quoteActionGuard.blocked}
              title={quoteActionGuard.reason}
            >
              <FileDown className='w-5 h-5' strokeWidth={2} />
              Gerar PDF
            </Button>

            <Button
              variant='secondary'
              size='sm'
              className='h-10 w-full group flex items-center justify-center gap-2 whitespace-nowrap bg-alba-green-500 text-alba-white-500 [&>svg]:text-alba-white-500 hover:[&>svg]:text-[#129440] md:w-auto'
              onClick={() => {
                setIsSendEmailOpen(true);
              }}
              disabled={isUnderAnalysis || quoteActionGuard.blocked}
              title={quoteActionGuard.reason}
            >
              <Mail className='w-5 h-5' strokeWidth={2} />
              E-mail
            </Button>

            <Button
              variant='primary'
              size='sm'
              className='relative flex h-10 w-full items-center justify-center whitespace-nowrap md:w-auto'
              title={proposalWriteGuard.reason}
              disabled={
                !canCreateProposal ||
                isUnderAnalysis ||
                proposalWriteGuard.blocked ||
                initiateProposalMutation.isPending ||
                validateInspectionJourneyMutation.isPending
              }
              onClick={handleCreateProposal}
            >
              <span
                className={
                  initiateProposalMutation.isPending ? 'opacity-0' : undefined
                }
              >
                Criar proposta
              </span>
              {initiateProposalMutation.isPending ? (
                <span className='absolute inset-0 flex items-center justify-center'>
                  <Spinner size='sm' colorScheme='gray' />
                </span>
              ) : null}
            </Button>

            <Button
              variant='primary'
              size='sm'
              className='flex h-10 w-full items-center justify-center gap-2 whitespace-nowrap md:w-auto'
              onClick={() => {
                window.history.back();
              }}
            >
              Sair
            </Button>
          </div>
        </div>

        <GeneratePdfDialog
          isOpen={isGeneratePdfOpen}
          onClose={() => setIsGeneratePdfOpen(false)}
          isLoading={generatePdfMutation.isPending}
          onConfirm={async () => {
            if (!policyId) {
              toaster.create({
                type: 'error',
                title: 'Não foi possível gerar o PDF',
                description: 'ID da cotação (PolicyId) não encontrado.',
              });
              return;
            }

            const resolvedProductCode = proposalData?.productCode;
            if (!resolvedProductCode) {
              toaster.create({
                type: 'error',
                title: 'Não foi possível gerar o PDF',
                description: 'Código do produto não encontrado.',
              });
              return;
            }

            try {
              const { url, key } = await generatePdfMutation.mutateAsync({
                policyId,
                productCode: resolvedProductCode,
                stage: 'quote',
              });
              // Faz download usando a URL já assinada
              const filename =
                key.split('/').pop() || `cotacao-${policyId}.pdf`;
              const response = await fetch(url);
              if (!response.ok) throw new Error('Falha ao baixar o PDF');
              const blob = await response.blob();
              downloadBlob(blob, filename);
              setIsGeneratePdfOpen(false);
            } catch (error) {
              toaster.create({
                type: 'error',
                title: 'Erro ao gerar PDF',
                description:
                  error instanceof Error ? error.message : 'Erro inesperado',
              });
            }
          }}
        />

        <InspectionJourneyModal
          isOpen={isInspectionJourneyModalOpen}
          onClose={() => setIsInspectionJourneyModalOpen(false)}
          onConfirm={handleInspectionJourneyConfirm}
          isLoading={
            validateInspectionJourneyMutation.isPending ||
            initiateProposalMutation.isPending
          }
        />

        <UnderAnalysisInfoModal
          isOpen={isUnderAnalysisModalOpen}
          onClose={() => setIsUnderAnalysisModalOpen(false)}
          messages={underAnalysisMessages}
        />

        <SendEmailDialog
          isOpen={isSendEmailOpen}
          onClose={() => setIsSendEmailOpen(false)}
          defaultBrokerEmail={attributes?.email || ''}
          isLoading={isSendingEmails || sendProposalEmailMutation.isPending}
          onConfirm={async values => {
            if (!policyId) {
              toaster.create({
                type: 'error',
                title: 'Não foi possível enviar o e-mail',
                description: 'ID da cotação (PolicyId) não encontrado.',
              });
              return;
            }

            const resolvedProductCode = proposalData?.productCode;
            if (!resolvedProductCode) {
              toaster.create({
                type: 'error',
                title: 'Não foi possível enviar o e-mail',
                description: 'Código do produto não encontrado.',
              });
              return;
            }

            try {
              setIsSendingEmails(true);

              const emails = Array.from(
                new Set(
                  [values.proponentEmail, values.brokerEmail]
                    .map(e => e.trim())
                    .filter(Boolean)
                )
              );

              for (const email of emails) {
                await sendProposalEmailMutation.mutateAsync({
                  policyId,
                  email,
                  productCode: resolvedProductCode,
                  stage: 'quote',
                });
              }

              toaster.create({
                type: 'success',
                title:
                  emails.length > 1
                    ? 'E-mails enviados com sucesso'
                    : 'E-mail enviado com sucesso',
              });
              setIsSendEmailOpen(false);
            } catch (error) {
              toaster.create({
                type: 'error',
                title: 'Erro ao enviar e-mail',
                description:
                  error instanceof Error ? error.message : 'Erro inesperado',
              });
            } finally {
              setIsSendingEmails(false);
            }
          }}
        />
      </div>
    );
  },
});
